import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import { createTheme } from "@mui/material";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { BUTTON_BASE_SX } from "./components/app/StyledButtonV2";
import { COMPONENT, PRIMITIVE } from "./tokens/design-tokens";

const p = PRIMITIVE;
const c = p.color;
export const INPUT_FIELD_HEIGHT = "34px";

/** Left inset of a field's value text — the `padding: 0 8px` on the input slot. */
const FIELD_TEXT_OFFSET = 8;

/**
 * A field rendered `readOnly`: a value being presented rather than captured.
 *
 * Which fields those are is decided in `components/ReadOnlyFields` — a preview
 * screen wraps itself once and the common field components render `readOnly`
 * instead of `disabled` inside it. This rule is the other half: the look.
 *
 * Matched from the `MuiFormControl` root so the field's label, value, outline
 * and adornments can all be reached, and deliberately narrow:
 *
 * - `> .MuiOutlinedInput-root` — only the outlined variant, so borderless
 *   `standard` fields keep their own look.
 * - `> input` / `> textarea` — only real text inputs, so controls that use
 *   `readOnly` to block typing while staying interactive keep their styling.
 *   The multi-select trigger renders `inputComponent: "div"` and is excluded
 *   by the tag on purpose.
 *
 * `disabled` is untouched and keeps the theme's grey treatment, so locked
 * fields on editable forms still read as locked.
 */
const READ_ONLY_FIELD = [
  "&:has(> .MuiOutlinedInput-root > input[readonly])",
  // A multiline field always renders a second, `aria-hidden` textarea that MUI
  // measures the row height against, and that mirror is hardcoded `readonly`.
  // Matching it would present every editable text area — stripping its border
  // and hiding its placeholder — so only the real input counts.
  "&:has(> .MuiOutlinedInput-root > textarea[readonly]:not([aria-hidden]))",
  // Opt-in for controls whose value slot is not a real input — the multi-select
  // trigger renders a div, so it marks itself rather than carrying `readonly`.
  '&:has(> .MuiOutlinedInput-root > [data-readonly="true"])',
].join(", ");

/**
 * Standardised non-editable look: brighter background fill, no border/stroke,
 * and the standard application text colours for both the value and the label.
 */
const READ_ONLY_FIELD_STYLE = {
  // Brighter fill, held through focus: a read-only field is still focusable
  // and would otherwise pick up the focused (selected) fill.
  "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.Mui-focused": {
    backgroundColor: COMPONENT.inputField.bg.default,
  },
  // No stroke, in any interaction state.
  "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: "transparent",
    },
  "& .MuiInputBase-input": {
    color: COMPONENT.inputField.text.label,
    WebkitTextFillColor: COMPONENT.inputField.text.label,
  },
  // An empty read-only field has nothing to prompt for — a placeholder reads as
  // an invitation to type into something that cannot be typed into. Leave the
  // field blank instead.
  //
  // MUI styles placeholders through vendor-prefixed pseudo-elements only, and
  // re-shows them on `:focus` — which now applies, because a read-only field is
  // still focusable. Each prefix needs its own rule: a selector list containing
  // one pseudo-element the engine doesn't know invalidates the whole rule.
  ...Object.fromEntries(
    [
      "::placeholder",
      "::-webkit-input-placeholder",
      "::-moz-placeholder",
      ":-ms-input-placeholder",
      "::-ms-input-placeholder",
    ].flatMap((ph) => [
      [`& .MuiInputBase-input${ph}`, { opacity: 0 }],
      [`& .MuiInputBase-input:focus${ph}`, { opacity: 0 }],
    ])
  ),
  "& .MuiInputLabel-root": {
    color: COMPONENT.inputField.text.label,
  },
  // Values start 8px in, while MUI floats the shrunk outlined label at 14px.
  // Pull the label back so label and value share a starting position.
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: `translate(${FIELD_TEXT_OFFSET}px, -9px) scale(0.75)`,
  },
  // A picker's calendar button is inert on a read-only field and would push
  // the date value out of line with its label.
  "& .MuiInputAdornment-positionStart .MuiIconButton-root": {
    display: "none",
  },
};

const theme = createTheme({
  typography: {
    fontFamily: p.typography.fontFamily,
  },
  palette: {
    primary: {
      main: c.brandBlue[700], // #104E84 — Brand
      light: c.brandBlue[25], // #EEF7FF
      dark: c.brandBlue[900], // #08345A
      contrastText: c.neutrals["000"],
    },
    secondary: {
      main: c.brandBlue[500], // #2771B1
      light: c.brandBlue[50], // #E5F3FF
      dark: c.brandBlue[800], // #0B406E
      contrastText: c.neutrals["000"],
    },
    error: {
      main: c.red[500], // #CE0718
      light: c.red[50], // #FFD4D8
      dark: c.red[700], // #8D000C
      contrastText: c.neutrals["000"],
    },
    warning: {
      main: c.orange[500], // #D66F00
      light: c.orange[50], // #FFF2D2
      dark: c.orange[700], // #943C00
      contrastText: c.neutrals["000"],
    },
    success: {
      main: c.green[500], // #17BF33
      light: c.green[50], // #E1FCDE
      dark: c.green[700], // #016A1C
      contrastText: c.neutrals["000"],
    },
    info: {
      main: c.brandBlue[600], // #175D9A
      light: c.brandBlue[100], // #AEDAFF
      dark: c.brandBlue[800], // #0B406E
      contrastText: c.neutrals["000"],
    },
    grey: {
      50: c.neutrals[200],
      100: c.neutrals[400],
      200: c.neutrals[700],
      300: c.neutrals[1000],
      400: c.neutrals[1200],
      500: c.neutrals[1700],
      600: c.neutrals[2000],
      700: c.neutrals[2200],
      800: c.neutrals[2400],
      900: c.neutrals[2600],
    },
    background: {
      default: c.neutrals[400], // #F5F5F5
      paper: c.neutrals["000"], // #FFFFFF
    },
    text: {
      primary: c.neutrals[2400], // #292929
      secondary: c.neutrals[2200], // #525252
      disabled: c.neutrals[1800], // #8F8F8F
    },
    divider: c.neutrals[1200], // #CCCCCC
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1536,
    },
  },
  components: {
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            height: INPUT_FIELD_HEIGHT,
            minHeight: INPUT_FIELD_HEIGHT,
            padding: "0 8px",
            fontSize: `${p.typography.fontSize.body.b5}px`,
          },
          "& .MuiAutocomplete-input": {
            padding: "0 !important",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        sx: BUTTON_BASE_SX,
      },
      styleOverrides: {
        root: ({ ownerState }) => ({
          // Base standard style for button with any variant
          ...(BUTTON_BASE_SX as any),
          textTransform: "none" as const,
          fontSize: `${p.typography.fontSize.body.b4}px`,
          borderRadius: p.radius[4],
          ...(ownerState.variant === "contained" && {
            backgroundColor: COMPONENT.button.primary.bg.default,
            ":hover": {
              backgroundColor: COMPONENT.button.primary.bg.hover,
            },
            ":active": {
              backgroundColor: COMPONENT.button.primary.bg.pressed,
            },
          }),
          ...(ownerState.variant === "outlined" && {
            borderColor: COMPONENT.button.secondary.outline.default,
            color: COMPONENT.button.secondary.text.default,
            ":hover": {
              borderColor: COMPONENT.button.secondary.outline.hover,
              backgroundColor: COMPONENT.button.secondary.bg.hover,
              color: COMPONENT.button.secondary.text.hover,
            },
          }),
          ...(ownerState.variant === "text" && {
            color: COMPONENT.button.tertiary.text.default,
            ":hover": {
              color: COMPONENT.button.tertiary.text.hover,
            },
          }),
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "&, [class^=MuiDataGrid]": {
            border: "none",
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
            outline: "none !important",
            backgroundColor: COMPONENT.table.bg.header,
          },
          "& .MuiDataGrid-virtualScroller": {
            marginTop: "0 !important",
          },
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: COMPONENT.table.bg.row1,
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: COMPONENT.table.bg.row2,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: `${p.radius[4]}px`,
          height: INPUT_FIELD_HEIGHT,
          "& .MuiSelect-select": {
            padding: "0 8px",
            fontSize: `${p.typography.fontSize.body.b5}px`,
            display: "flex",
            alignItems: "center",
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: "10px",
          marginRight: "6px",
          width: 32,
          height: 16,
          padding: 0,
          backgroundColor: "transparent",
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2px",
            transitionDuration: "300ms",
            color: "#08345A",
            "&:hover": { backgroundColor: "transparent" },
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: "#08345A",
              "& + .MuiSwitch-track": {
                backgroundColor: "#AEDAFF !important",
                opacity: 1,
                border: "2px solid #AEDAFF",
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.2,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: c.green[400],
              border: `6px solid ${c.neutrals["000"]}`,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color:
                theme.palette.mode === "light"
                  ? theme.palette.grey[100]
                  : theme.palette.grey[600],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 12,
            height: 12,
          },
          "& .MuiSwitch-track": {
            borderRadius: 8,
            backgroundColor: "#fff !important",
            border: "1px solid #7A7A7A",
            opacity: "1 !important",
            height: "100%",
            boxSizing: "border-box",
            transition: theme.transitions.create(["background-color"], {
              duration: 100,
            }),
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          [READ_ONLY_FIELD]: READ_ONLY_FIELD_STYLE,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: `${p.typography.fontSize.body.b6}px`,
          lineHeight: "14px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: ownerState?.style?.height ?? INPUT_FIELD_HEIGHT,
          display: "flex",
          alignItems: "center",
          backgroundColor: COMPONENT.inputField.bg.default,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.default,
            "& legend": {
              fontSize: "0.6em",
              "& span": {
                padding: "0px",
              },
            },
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.hover,
          },
          "&.Mui-focused": {
            backgroundColor: COMPONENT.inputField.bg.selected,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.selected,
              borderWidth: "1px",
            },
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.error,
          },
          "&.Mui-disabled": {
            backgroundColor: COMPONENT.inputField.bg.disabled,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.disabled,
            },
          },
        }),
        input: {
          padding: "0 8px",
          fontSize: `${p.typography.fontSize.body.b5}px`,
          "&::placeholder": {
            fontSize: `${p.typography.fontSize.body.b5}px`,
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: ownerState?.style?.height ?? INPUT_FIELD_HEIGHT,
          display: "flex",
          alignItems: "center",
          backgroundColor: COMPONENT.inputField.bg.default,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.default,
            "& legend": {
              fontSize: "0.6em",
              "& span": {
                padding: "0px",
              },
            },
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.hover,
          },
          "&.Mui-focused": {
            backgroundColor: COMPONENT.inputField.bg.selected,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.selected,
              borderWidth: "1px",
            },
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: COMPONENT.inputField.outline.error,
          },
          "&.Mui-disabled": {
            backgroundColor: COMPONENT.inputField.bg.disabled,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: COMPONENT.inputField.outline.disabled,
            },
          },
        }),
        input: {
          padding: "0 8px",
          fontSize: `${p.typography.fontSize.body.b5}px`,
          "&::placeholder": {
            fontSize: `${p.typography.fontSize.body.b5}px`,
          },
        },
      },
    },
    MuiDatePicker: {
      defaultProps: {
        slotProps: {
          textField: {
            size: "small",
            InputLabelProps: { shrink: true },
            sx: {
              "& .MuiOutlinedInput-root": {
                height: INPUT_FIELD_HEIGHT,
                minHeight: INPUT_FIELD_HEIGHT,
                padding: "0 8px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "0 !important",
                fontSize: `${p.typography.fontSize.body.b5}px`,
              },
            },
          },
        },
      },
    },
    MuiTimePicker: {
      defaultProps: {
        slotProps: {
          textField: {
            size: "small",
            InputLabelProps: { shrink: true },
            sx: {
              "& .MuiOutlinedInput-root": {
                height: INPUT_FIELD_HEIGHT,
                minHeight: INPUT_FIELD_HEIGHT,
                padding: "0 8px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "0 !important",
                fontSize: `${p.typography.fontSize.body.b5}px`,
              },
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.variant === "h6" && {
            fontSize: "1.65rem",
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h5" && {
            fontSize: `${p.typography.fontSize.heading.h4}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h3" && {
            fontSize: `${p.typography.fontSize.title.t2}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "h4" && {
            fontSize: `${p.typography.fontSize.title.t3}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "body2" && {
            fontWeight: p.typography.fontWeight.regular,
            fontSize: `${p.typography.fontSize.body.b3}px`,
          }),
          ...(ownerState.variant === "subtitle2" && {
            fontSize: `${p.typography.fontSize.body.b4}px`,
            fontWeight: p.typography.fontWeight.regular,
            color: c.neutrals[2500] + "66",
          }),
          ...(ownerState.variant === "subtitle1" && {
            fontSize: `${p.typography.fontSize.body.b5}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
          ...(ownerState.variant === "button" && {
            fontSize: `${p.typography.fontSize.body.b3}px`,
            fontWeight: p.typography.fontWeight.regular,
          }),
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, .05)"
              : "rgba(0, 0, 0, .03)",
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: `${p.typography.fontSize.body.b5}px !important`,
          textTransform: "none" as const,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: "12px !important",
        },
        label: {
          fontSize: "12px",
        },
      },
    },
    MuiBadge: {
      defaultProps: {
        max: 9999,
      },
    },
  },
});

export default theme;
